import {
  App,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  hrDepthApi,
  type CriteriaScoreInput,
  type EvaluationCriteria,
  type PerformanceEvaluation,
} from '@/api/hr-depth';
import { hrApi } from '@/api/hr';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const LABEL_OPTIONS = ['Excellent', 'Good', 'Average', 'Poor'];

interface EditorShape {
  staffId: number;
  evaluationPeriod?: string;
  remarks?: string;
  sharedWithStaff: boolean;
  scores: Record<number, { score?: number | null; label?: string | null }>;
}

export function PerformanceEvaluationsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('HR_MGMT:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));

  const [yearId, setYearId] = useState<number | undefined>();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<PerformanceEvaluation | null>(null);
  const [form] = Form.useForm<EditorShape>();

  const yearsQuery = useQuery({
    queryKey: ['setup', 'academic-years'],
    queryFn: () => setupApi.listAcademicYears(),
  });

  // Default to the current academic year on first paint.
  useEffect(() => {
    if (!yearId && yearsQuery.data) {
      const cur = yearsQuery.data.find((y) => y.current) ?? yearsQuery.data[0];
      if (cur) setYearId(cur.id);
    }
  }, [yearId, yearsQuery.data]);

  const evals = useQuery({
    queryKey: ['hr', 'evaluations', yearId],
    queryFn: () => hrDepthApi.listEvalsForYear(yearId!),
    enabled: !!yearId,
  });

  const criteria = useQuery({
    queryKey: ['hr', 'evaluation-criteria', 'active'],
    queryFn: () => hrDepthApi.listCriteria(true),
  });

  const staff = useQuery({
    queryKey: ['hr', 'staff', 'active', 500],
    queryFn: () => hrApi.searchStaff({ active: true, page: 0, size: 500 }),
  });

  const save = useMutation({
    mutationFn: (v: EditorShape) => {
      const scores: CriteriaScoreInput[] = (criteria.data ?? [])
        .filter((c) => v.scores[c.id])
        .map((c) => ({
          criteriaId: c.id,
          criteriaName: c.name,
          score: c.ratingType === 'NUMERIC' ? v.scores[c.id]?.score ?? null : null,
          label: c.ratingType === 'LABEL' ? v.scores[c.id]?.label ?? null : null,
        }));
      const body = {
        staffId: v.staffId,
        academicYearId: yearId!,
        evaluationPeriod: v.evaluationPeriod || null,
        remarks: v.remarks || null,
        sharedWithStaff: !!v.sharedWithStaff,
        criteriaScores: scores,
      };
      return editing ? hrDepthApi.updateEvaluation(editing.id, body) : hrDepthApi.createEvaluation(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Evaluation updated' : 'Evaluation recorded');
      qc.invalidateQueries({ queryKey: ['hr', 'evaluations'] });
      setEditorOpen(false);
      setEditing(null);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e, 'Save failed')),
  });

  const toggleShared = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) => hrDepthApi.setEvaluationShared(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'evaluations'] }),
    onError: (e) => message.error(extractError(e, 'Update failed')),
  });

  const startCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ scores: {}, sharedWithStaff: false });
    setEditorOpen(true);
  };

  const startEdit = (row: PerformanceEvaluation) => {
    setEditing(row);
    const scores: Record<number, { score?: number | null; label?: string | null }> = {};
    row.criteriaScores.forEach((s) => {
      scores[s.criteriaId] = {
        score: s.score === null ? null : Number(s.score),
        label: s.label,
      };
    });
    form.setFieldsValue({
      staffId: row.staffId,
      evaluationPeriod: row.evaluationPeriod ?? undefined,
      remarks: row.remarks ?? undefined,
      sharedWithStaff: row.sharedWithStaff,
      scores,
    });
    setEditorOpen(true);
  };

  const columns: TableProps<PerformanceEvaluation>['columns'] = [
    {
      title: 'Staff',
      dataIndex: 'staffName',
      width: 180,
      render: (v: string | null) => <Typography.Text strong>{v ?? '—'}</Typography.Text>,
    },
    {
      title: 'Period',
      dataIndex: 'evaluationPeriod',
      width: 120,
      render: (v: string | null) => v ?? <Tag>Annual</Tag>,
    },
    {
      title: 'Overall',
      width: 140,
      render: (_, row) =>
        row.overallScore !== null ? (
          <Tag color="blue">{Number(row.overallScore).toFixed(2)} / 5</Tag>
        ) : row.overallLabel ? (
          <Tag color="green">{row.overallLabel}</Tag>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Recorded',
      dataIndex: 'updatedAt',
      width: 160,
      render: (v: string) => dayjs(v).format('D MMM YYYY HH:mm'),
    },
    {
      title: 'Evaluator',
      dataIndex: 'evaluatedByUsername',
      width: 130,
      render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Shared with staff',
      dataIndex: 'sharedWithStaff',
      width: 130,
      render: (v: boolean, row) => (
        <Switch
          checked={v}
          disabled={!canEdit}
          onChange={(value) => toggleShared.mutate({ id: row.id, value })}
        />
      ),
    },
    {
      title: '',
      width: 80,
      render: (_, row) =>
        canEdit && (
          <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(row)}>
            Edit
          </Button>
        ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="HR"
        title="Performance evaluations"
        description="Per-staff evaluations against the criteria configured under HR setup. Numeric scores roll up to a 0–5 overall; pure-label evaluations show the dominant label."
        actions={
          canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={startCreate}
              disabled={!yearId || !(criteria.data?.length ?? 0)}
            >
              New evaluation
            </Button>
          )
        }
      />

      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={9}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Academic year
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              loading={yearsQuery.isLoading}
              value={yearId}
              onChange={setYearId}
              options={(yearsQuery.data ?? []).map((y) => ({
                value: y.id,
                label: y.name + (y.current ? ' (current)' : ''),
              }))}
            />
          </Col>
        </Row>
      </Card>

      {(criteria.data?.length ?? 0) === 0 && (
        <Card>
          <Empty description="No active evaluation criteria. Configure them under HR → Evaluation criteria first." />
        </Card>
      )}

      {evals.data && (criteria.data?.length ?? 0) > 0 && (
        <Card>
          {evals.data.length === 0 ? (
            <Empty description="No evaluations recorded for this year yet." />
          ) : (
            <Table<PerformanceEvaluation>
              rowKey="id"
              dataSource={evals.data}
              loading={evals.isFetching}
              columns={columns}
              pagination={{ pageSize: 25, showSizeChanger: true }}
              size="small"
              scroll={{ x: 900 }}
            />
          )}
        </Card>
      )}

      <Modal
        open={editorOpen}
        title={editing ? `Edit evaluation — ${editing.staffName}` : 'New performance evaluation'}
        onCancel={() => {
          setEditorOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
        destroyOnHidden
        width={640}
      >
        <Form<EditorShape> form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
          <Form.Item label="Staff member" name="staffId" rules={[{ required: true }]}>
            <Select
              placeholder="Choose staff"
              loading={staff.isLoading}
              options={(staff.data?.rows ?? []).map((s) => ({
                value: s.id,
                label: `${s.firstName} ${s.lastName}` + (s.designationName ? ` · ${s.designationName}` : ''),
              }))}
              showSearch
              optionFilterProp="label"
              disabled={!!editing}
            />
          </Form.Item>
          <Form.Item label="Evaluation period (optional)" name="evaluationPeriod">
            <Input placeholder="e.g. Annual, Term 1, Term 2" maxLength={50} />
          </Form.Item>

          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Criteria scores
          </Typography.Text>
          <Card size="small" style={{ marginTop: 6, marginBottom: 12 }}>
            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
              {(criteria.data ?? []).map((c) => (
                <div key={c.id}>
                  <Typography.Text strong>{c.name}</Typography.Text>
                  <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    {c.ratingType === 'NUMERIC' ? `out of ${c.maxScore ?? 5}` : 'pick a grade'}
                  </Typography.Text>
                  <ScoreInput criterion={c} form={form} />
                </div>
              ))}
            </Space>
          </Card>

          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={3} maxLength={5000} />
          </Form.Item>
          <Form.Item label="Share with staff member" name="sharedWithStaff" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function ScoreInput({
  criterion,
}: {
  criterion: EvaluationCriteria;
  form: ReturnType<typeof Form.useForm<EditorShape>>[0];
}) {
  if (criterion.ratingType === 'NUMERIC') {
    return (
      <Form.Item
        name={['scores', criterion.id, 'score']}
        rules={[
          {
            required: true,
            type: 'number',
            min: 0,
            max: criterion.maxScore ?? 5,
            message: `Score must be 0–${criterion.maxScore ?? 5}`,
          },
        ]}
      >
        <InputNumber min={0} max={criterion.maxScore ?? 5} step={0.5} style={{ width: 200, marginTop: 4 }} />
      </Form.Item>
    );
  }
  return (
    <Form.Item name={['scores', criterion.id, 'label']} rules={[{ required: true, message: 'Pick a grade' }]}>
      <Select
        style={{ width: 240, marginTop: 4 }}
        placeholder="Choose grade"
        options={LABEL_OPTIONS.map((l) => ({ value: l, label: l }))}
      />
    </Form.Item>
  );
}
